/**
 * Notification feature — pure formatting helpers.
 *
 * No React, no Supabase. Safe to import from anywhere.
 */

/**
 * Formats a `YYYY-MM-DD` string as "Monday, Apr 1" in local time.
 * Used by the attendance-confirmation card in the bell.
 */
export function formatAttendanceDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "long", month: "short", day: "numeric",
  });
}

/**
 * Collapses an ISO timestamp to a short relative-time label:
 *   "just now" | "5m ago" | "3h ago" | "2d ago"
 */
export function relativeTime(iso: string): string {
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  < 1)  return "just now";
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

// ─────────────────────────────────────────────
// action_url → event_id
//
// Two legacy shapes in circulation:
//
//   • Phase 16 RPCs stored the raw event UUID in `action_url`, e.g.
//     "7f2b3a9e-…-1234".
//   • The Phase 25 `on_event_reschedule` trigger stores a navigable path,
//     e.g. "/events/7f2b3a9e-…-1234".
//
// `extractEventId` normalises both so Accept/Decline handlers don't
// branch on notification type.
// ─────────────────────────────────────────────
export const UUID_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

export function extractEventId(actionUrl: string | null | undefined): string | null {
  if (!actionUrl) return null;
  const match = actionUrl.match(UUID_RE);
  return match ? match[1] : null;
}
